import { useEffect, useState } from "react";
import { Link, } from "react-router-dom";
import { API_URL } from "../../shared/constant";
import { toast } from "react-toastify";
import axios from "axios";
import Swal from "sweetalert2";
import Loader from "../../shared/loader";
import { isPWA, isMobilePWA } from "../../utils/pwa";
import { isWebView } from "../../utils/webview";
import "../../styles/pwa-customers.css";

const AddContactPersons = () => {
    const [loading, setLoading] = useState(true);
    const [contactpersonsData, setContactPersonsData] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [pwaMode, setPwaMode] = useState(false);
    const [mobilePWA, setMobilePWA] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [refreshing, setRefreshing] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [webViewMode, setWebViewMode] = useState(false);

    const fetchTermsData = () => {
        axios.post(API_URL.CONTACT_PERSON_LIST, {}).then(
            (res) => {
                if (res.data.status === 1) {
                    setContactPersonsData(res.data.data);
                }
                setLoading(false);
                setRefreshing(false);
                if (sessionStorage.getItem("pId")) {
                    let rId = "row-" + JSON.parse(sessionStorage.getItem("pId"));
                    setTimeout(() => {
                        scrollToRow(rId);
                    }, 100);
                }
            },
            (error) => {
                setLoading(false);
                setRefreshing(false);
                toast.error("Something went wrong..", {
                    autoClose: 3000,
                });
            }
        );
    };

    const deleteState = (row) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You want to delete this - " + row.name,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                setLoading(true);
                axios.post(API_URL.CONTACT_PERSON_DELETE, { id: row.id }).then(
                    (res) => {
                        if (res.data.status === 1) {
                            let newData = contactpersonsData.filter((r) => r.id != row.id);
                            setContactPersonsData(newData);
                        }
                        toast.success(row.name + " Delete Successfully", { autoClose: 3000 });
                        setLoading(false);
                    },
                    (error) => {
                        setLoading(false);
                        toast.error("Something went wrong..", { autoClose: 3000 });
                    }
                );
            }
        });
    };

    const scrollToRow = (rowId) => {
        const rowElement = document.getElementById(rowId);
        if (rowElement) {
            rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    };
    
    // Handle online/offline status
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            if (pwaMode) {
                toast.success("Connection restored", { autoClose: 2000 });
                fetchTermsData(); // Refresh data when back online
            }
        };
        
        const handleOffline = () => {
            setIsOnline(false);
            if (pwaMode) {
                toast.warning("You're offline. Some features may not work.", { autoClose: 3000 });
            }
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [pwaMode]);

    // Pull to refresh functionality
    useEffect(() => {
        if (!pwaMode || !mobilePWA) return;

        let startY = 0;
        let currentY = 0;
        let isPulling = false;
        let pullElement = null;

        const handleTouchStart = (e) => {
            if (window.scrollY === 0) {
                startY = e.touches[0].clientY;
                pullElement = document.querySelector('.pwa-contactpersons-list');
            }
        };

        const handleTouchMove = (e) => {
            if (window.scrollY === 0 && pullElement) {
                currentY = e.touches[0].clientY;
                const pullDistance = currentY - startY;

                if (pullDistance > 50) {
                    isPulling = true;
                    pullElement.style.transform = `translateY(${Math.min(pullDistance * 0.3, 50)}px)`;
                    pullElement.style.opacity = '0.7';
                }
            }
        };

        const handleTouchEnd = () => {
            if (isPulling && pullElement) {
                pullElement.style.transform = '';
                pullElement.style.opacity = '';
                
                setRefreshing(true);
                fetchTermsData().finally(() => {
                    setRefreshing(false);
                    toast.success("Data refreshed", { autoClose: 1500 });
                });
                
                isPulling = false;
            }
        };

        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: true });
        document.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [pwaMode, mobilePWA]);

    useEffect(() => {
        fetchTermsData();
        // Check PWA mode, mobile, and webview
        setPwaMode(isPWA());
        setMobilePWA(isMobilePWA());
        setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
        setWebViewMode(isWebView());
    }, []);
    return (
        <>
            {/* PWA Header - Only show in PWA mode or mobile (not webview) */}
            {(pwaMode || (isMobile && !webViewMode)) && (
                <div className="pwa-header bg-primary text-white p-3 mb-3">
                    <div className="d-flex align-items-center justify-content-between">
                        <h4 className="mb-0">
                            <i className="simple-icon-people me-2"></i>
                            Contact Persons ({contactpersonsData.length})
                        </h4>
                        <div className="d-flex align-items-center gap-2">
                            {!isOnline && (
                                <div className="pwa-status-indicator bg-warning">
                                    <i className="simple-icon-wifi me-1"></i>
                                    Offline
                                </div>
                            )}
                            {mobilePWA && (
                                <div className="pwa-status-indicator">
                                    <i className="simple-icon-screen-smartphone me-1"></i>
                                    PWA
                                </div>
                            )}
                            {refreshing && (
                                <div className="pwa-status-indicator bg-info">
                                    <i className="simple-icon-refresh me-1"></i>
                                    Refreshing
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            <div className={`row ${(pwaMode || (isMobile && !webViewMode)) ? 'pwa-content' : ''}`}>
                {!(pwaMode || (isMobile && !webViewMode)) && (
                    <>
                        <div className="col-6">
                            <h1>
                                Contact Persons
                                <small className="me-2">({contactpersonsData.length})</small>
                            </h1>
                            {webViewMode && (
                                <small className="text-muted">
                                    <i className="simple-icon-screen-smartphone me-1"></i>
                                    WebView Mode
                                </small>
                            )}
                        </div>
                        <div className="col-6">
                            <div className="d-flex align-items-center  justify-content-end">
                                <Link to={"/settings/contact-persons/add"} className="btn btn-primary">
                                    <i className="simple-icon-plus me-2" aria-hidden="true"></i> Add
                                    Contact Persons
                                </Link>
                            </div>
                        </div>
                    </>
                )}
                
                {(pwaMode || (isMobile && !webViewMode)) && (
                    <div className="col-12">
                        <div className="pwa-search-section mb-3">
                            <div className="input-group">
                                <input
                                    type="text"
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    className="form-control form-control-lg"
                                    placeholder="Search by name..."
                                />
                                <button className="btn btn-outline-secondary" type="button">
                                    <i className="simple-icon-magnifier"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                <div className="col-sm-12">
                    {(pwaMode || (isMobile && !webViewMode)) ? (
                        // PWA Mobile Card Layout
                        <div className="pwa-contactpersons-list">
                            {pwaMode && mobilePWA && (
                                <div className="pwa-pull-hint text-center text-muted mb-3">
                                    <small>
                                        <i className="simple-icon-arrow-down me-1"></i>
                                        Pull down to refresh
                                    </small>
                                </div>
                            )}
                            {loading && <Loader></Loader>}
                            {!loading && (
                                <div>
                                    {contactpersonsData.filter(row => 
                                        !searchValue || 
                                        row.name?.toLowerCase().includes(searchValue.toLowerCase())
                                    ).map((row, key) => {
                                        return (
                                            <div key={key} className="pwa-contactpersons-card mb-3">
                                                <div className="card">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div>
                                                                <h6 className="card-title mb-1">
                                                                    #{key + 1}
                                                                </h6>
                                                                <small className="text-muted">ID: {row.id}</small>
                                                            </div>
                                                            <div className="pwa-status-badge">
                                                                <span className="badge bg-info">Contact</span>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="pwa-contactpersons-details mb-3">
                                                            <div className="row">
                                                                <div className="col-12">
                                                                    <small className="text-muted d-block">Name</small>
                                                                    <small className="fw-bold">{row.name}</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="pwa-actions">
                                                            <div className="btn-group w-100" role="group">
                                                                <Link 
                                                                    to={"/settings/contact-persons/edit/" + row.id} 
                                                                    className="btn btn-outline-primary btn-sm"
                                                                >
                                                                    <i className="simple-icon-pencil me-1"></i> Edit
                                                                </Link>
                                                                <button 
                                                                    onClick={() => deleteState(row)} 
                                                                    className="btn btn-outline-danger btn-sm"
                                                                >
                                                                    <i className="simple-icon-trash me-1"></i> Delete
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    
                                    {contactpersonsData.filter(row => 
                                        !searchValue || 
                                        row.name?.toLowerCase().includes(searchValue.toLowerCase())
                                    ).length === 0 && (
                                        <div className="text-center py-5">
                                            <i className="simple-icon-people" style={{fontSize: '3rem'}}></i>
                                            <h5>No contact persons found</h5>
                                            <p className="text-muted">Start by adding your first contact person</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        // Original Desktop Table Layout
                        <div className="card">
                            <div className="card-body p-0">
                                {loading && <Loader></Loader>}
                                {!loading && (
                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead className="">
                                                <tr>
                                                    <th>#</th>
                                                    <th>Name</th>
                                                    <th className="text-center" style={{ width: "200px" }}>
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {contactpersonsData.filter(row => 
                                                    !searchValue || 
                                                    row.name?.toLowerCase().includes(searchValue.toLowerCase())
                                                ).map((row, key) => {
                                                    return (
                                                        <tr key={key}>
                                                            <td style={{ width: "10px" }}>{key + 1}</td>
                                                            <td>
                                                                {row.name}
                                                            </td>
                                                            <td
                                                                className="d-flex justify-content-center gap-2"
                                                                style={{ width: "200px" }}
                                                            >
                                                                <Link
                                                                    to={"/settings/contact-persons/edit/" + row.id}
                                                                    className="btn btn-sm btn-secondary mr-2"
                                                                >
                                                                    <i className="simple-icon-pencil me-1"></i> Edit
                                                                </Link>
                                                                <button
                                                                    onClick={() => deleteState(row)}
                                                                    className="btn btn-sm btn-danger"
                                                                >
                                                                    <i className="simple-icon-trash me-1"></i> Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* PWA Floating Action Button */}
            {(pwaMode || (isMobile && !webViewMode)) && (
                <Link 
                    to={"/settings/contact-persons/add"} 
                    className="btn btn-primary pwa-fab position-fixed rounded-circle shadow-lg"
                    style={{
                        bottom: '100px',
                        right: '20px',
                        width: '60px',
                        height: '60px',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px'
                    }}
                >
                    <i className="simple-icon-plus"></i>
                </Link>
            )}
        </>
    );
};

export default AddContactPersons;
